import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  fieldWorks as api, clients as clientsApi,
  type FieldWork, type FieldWorkInvoiceLink, type FieldWorkMeta, type FieldWorkStatusCode,
} from '@/api/endpoints';
import type { Client } from '@/types';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { PracaFormModal } from '@/components/PracaFormModal';
import { WorkOrderTimelineCard } from '@/components/WorkOrderTimelineCard';
import { OverviewMap, type OverviewMarker } from '@/components/OverviewMap';
import { formatCurrency } from '@/lib/money';

/**
 * Karta pracy terenowej (V154): nagłówek, terminy wykonania, lokalizacja i rozliczenie.
 *
 * Sekcja faktur jest tu SEDNEM modułu — to ona zamyka pytanie „za co wystawiliśmy tę fakturę".
 * System niczego nie przypina sam: pokazuje kandydatów (faktury sprzedażowe tego klienta),
 * a decyzję zostawia człowiekowi — ten sam wzorzec co podpowiedzi KSeF przy subskrypcjach.
 */
export function PracaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const workId = id ? Number(id) : null;
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'FIELDWORK_EDIT');
  const canSeePricing = hasPermission(user, 'PRICING_VIEW');
  const canSeeFinance = hasPermission(user, 'FINANCE_VIEW');
  const canSeeOffers = hasPermission(user, 'OFFER_VIEW');
  const canSeeContracts = hasPermission(user, 'CONTRACT_VIEW');
  const canEditOrders = hasPermission(user, 'FIELDWORK_ORDER_EDIT');

  const [work, setWork] = useState<FieldWork | null>(null);
  const [meta, setMeta] = useState<FieldWorkMeta | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [candidates, setCandidates] = useState<FieldWorkInvoiceLink[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    if (!workId) return;
    setErr(null);
    api.get(workId).then(setWork).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { setWork(null); reload(); /* eslint-disable-next-line */ }, [workId]);
  useEffect(() => {
    api.meta().then(setMeta).catch(() => {});
    clientsApi.list().then(setClients).catch(() => {});
  }, []);

  const openPicker = () => {
    if (!workId) return;
    setPickerOpen(true);
    setCandidates(null);
    api.invoiceCandidates(workId).then(setCandidates).catch(e => setErr(e?.message ?? String(e)));
  };

  const attach = (invoiceId: number) => {
    if (!workId) return;
    api.attachInvoice(workId, invoiceId)
      .then(() => { setPickerOpen(false); reload(); })
      .catch(e => setErr(e?.message ?? String(e)));
  };

  const detach = (linkId: number) => {
    api.detachInvoice(linkId).then(reload).catch(e => setErr(e?.message ?? String(e)));
  };

  const remove = () => {
    if (!work) return;
    if (!confirm(`Usunąć pracę #${work.seqNo} „${work.title}"? Operacji nie da się cofnąć.`)) return;
    api.remove(work.id).then(() => nav('/prace')).catch(e => setErr(e?.message ?? String(e)));
  };

  if (err && !work) return <div className="text-red-600">Błąd: {err}</div>;
  if (!work) return <div className="text-ink-400">Ładowanie…</div>;

  const markers: OverviewMarker[] = work.mapLatitude != null && work.mapLongitude != null ? [{
    id: `w-${work.id}`,
    type: 'intake',
    lat: Number(work.mapLatitude),
    lng: Number(work.mapLongitude),
    label: work.title,
    color: meta?.statuses.find(s => s.id === work.status)?.mapColor,
    popupHtml: `<div style="font-family:system-ui;font-size:13px;font-weight:600;">${escapeHtml(work.title)}</div>`,
  }] : [];

  return (
    <div className="max-w-[1300px]">
      <header className="flex items-start justify-between mb-5 gap-4">
        <div className="min-w-0">
          <div className="text-[12px] text-ink-500 mb-1">
            <NavLink to="/prace" className="hover:text-ink-800">Prace terenowe</NavLink>
            <span className="mx-1.5">/</span>
            <span className="text-ink-700">#{work.seqNo}</span>
          </div>
          <h1 className="m-0">{work.title}</h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-500 mt-2">
            <span className={'pill pill-' + work.statusColor}>{work.statusName}</span>
            <span className="pill pill-earth">{work.kindName}</span>
            {work.clientId && (
              <NavLink to={`/klienci/${work.clientId}`} className="text-water-600 hover:underline">
                {work.clientName}
              </NavLink>
            )}
            {work.wellLabel && <span>studnia {work.wellLabel}</span>}
            {work.locationText && <span>{work.locationText}</span>}
            {work.parcel && <span className="font-mono">dz. {work.parcel}</span>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {canEdit && (
            <div className="w-[160px]">
              <SearchableSelect value={work.status}
                onChange={v => api.changeStatus(work.id, v as FieldWorkStatusCode).then(setWork)
                  .catch(e => setErr(e?.message ?? String(e)))}>
                {(meta?.statuses ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </SearchableSelect>
            </div>
          )}
          {canEdit && (
            <button onClick={() => setEditOpen(true)} className="btn btn-secondary btn-sm">
              <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj
            </button>
          )}
          {canEdit && (
            <button onClick={remove} className="btn btn-ghost btn-sm text-red-600" title="Usuń pracę">
              <Icon name="x" className="w-3.5 h-3.5"/>
            </button>
          )}
          <NavLink to="/prace" className="btn btn-secondary btn-sm">← Rejestr</NavLink>
        </div>
      </header>

      {err && <div className="text-red-600 p-3 bg-red-50 border border-red-200 rounded mb-3">{err}</div>}

      <div className="grid grid-cols-[1fr_360px] gap-4 items-start">
        <div className="grid gap-4">
          {work.orderId && <WorkOrderTimelineCard orderId={work.orderId} workId={work.id} canEdit={canEdit}/>}
          {/* ── Terminy wykonania ─────────────────────────────────────────────── */}
          <div className="card">
            <div className="card-head">
              <div className="card-title">Terminy wykonania</div>
              <span className="ml-auto text-[12px] text-ink-400">
                {work.dates.length} {work.dates.length === 1 ? 'wpis' : 'wpisy'} · {work.totalDays} dni
              </span>
            </div>
            <div className="card-body !p-0">
              {work.dates.length === 0 && <div className="p-4 text-ink-400">Brak wpisanych terminów.</div>}
              {work.dates.length > 0 && (
                <table className="table-hw">
                  <thead><tr><th>#</th><th>Od</th><th>Do</th><th>Dni</th><th>Uwaga</th></tr></thead>
                  <tbody>
                    {work.dates.map(d => (
                      <tr key={d.id ?? d.seq}>
                        <td className="font-mono text-[12px] text-ink-400">{d.seq}</td>
                        {d.dateFrom ? (
                          <>
                            <td className="font-mono text-[12px]">{d.dateFrom}</td>
                            <td className="font-mono text-[12px] text-ink-600">{d.dateTo ?? '—'}</td>
                            <td className="text-[12px]">{d.dayCount}</td>
                          </>
                        ) : (
                          // Zapis z importu, którego nie dało się rozłożyć na daty — pokazujemy
                          // oryginał zamiast udawać, że wpisu nie ma.
                          <td colSpan={3} className="text-[12px] text-amber-700 italic">
                            <Icon name="bell" className="w-3 h-3 inline mr-1"/>
                            zapis nierozpoznany: „{d.rawText}" — popraw w edycji
                          </td>
                        )}
                        <td className="text-[12px] text-ink-600">{d.note ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Faktury ───────────────────────────────────────────────────────── */}
          {canSeeFinance && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Faktury za tę pracę</div>
                {canEdit && (
                  <button onClick={openPicker} className="ml-auto btn btn-secondary btn-sm">
                    <Icon name="plus" className="w-3.5 h-3.5"/> Przypnij fakturę
                  </button>
                )}
              </div>
              <div className="card-body !p-0">
                {(work.invoices ?? []).length === 0 && (
                  <div className="p-4 text-ink-400 text-[13px]">
                    Brak przypiętych faktur.
                    {work.invoicedDate && (
                      <span className="text-ink-600">
                        {' '}W arkuszu widniała data faktury <strong>{work.invoicedDate}</strong> —
                        dokumentu z tamtego okresu nie ma w systemie.
                      </span>
                    )}
                  </div>
                )}
                {(work.invoices ?? []).length > 0 && (
                  <table className="table-hw">
                    <thead>
                      <tr><th>Numer</th><th>Wystawiona</th><th>Netto</th><th>Brutto</th><th>Płatność</th><th></th></tr>
                    </thead>
                    <tbody>
                      {(work.invoices ?? []).map(l => (
                        <tr key={l.id} className="hover:bg-ink-50">
                          <td>
                            <NavLink to={`/faktury/${l.invoiceId}`}
                                     className="font-mono text-[12px] text-water-600 hover:underline">
                              {l.invoiceNumber ?? l.ksefNumber ?? `#${l.invoiceId}`}
                            </NavLink>
                          </td>
                          <td className="font-mono text-[12px] text-ink-600">{l.issueDate ?? '—'}</td>
                          <td className="font-mono text-[12px]">{formatCurrency(l.netAmount ?? l.totalNet)}</td>
                          <td className="font-mono text-[12px]">{formatCurrency(l.totalGross)}</td>
                          <td><span className={'pill pill-' + l.paymentStatusColor}>{l.paymentStatusName}</span></td>
                          <td className="text-right">
                            {canEdit && (
                              <button onClick={() => detach(l.id)} className="btn btn-ghost btn-sm text-ink-400 hover:text-red-600"
                                      title="Odepnij fakturę od pracy">
                                <Icon name="x" className="w-3.5 h-3.5"/>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {(work.description || work.notes) && (
            <div className="card">
              <div className="card-head"><div className="card-title">Opis i uwagi</div></div>
              <div className="card-body text-[13px] text-ink-700 whitespace-pre-wrap">
                {work.description}
                {work.notes && (
                  <div className="mt-3 pt-3 border-t border-ink-100 text-ink-600 italic">{work.notes}</div>
                )}
              </div>
            </div>
          )}
        </div>

        <aside className="grid gap-4">
          <div className="card">
            <div className="card-head"><div className="card-title">Terminy i rozliczenie</div></div>
            <div className="card-body text-[13px] grid gap-2">
              <Row label="Termin z umowy" value={work.contractDeadline ?? work.deadlineNote ?? '—'}
                   tone={work.daysToDeadline != null && work.daysToDeadline < 0 ? 'red' : undefined}
                   hint={work.daysToDeadline == null ? undefined
                     : work.daysToDeadline < 0 ? `po terminie o ${-work.daysToDeadline} dni`
                     : `pozostało ${work.daysToDeadline} dni`}/>
              <Row label="Planowane rozpoczęcie" value={work.plannedDate ?? '—'}/>
              <Row label="Wykonano" value={work.firstDate ? `${work.firstDate} – ${work.lastDate}` : '—'}/>
              {canSeePricing && <Row label="Wartość netto" value={formatCurrency(work.netAmount)}/>}
              <Row label="Rozliczenie" value={work.settled ? 'Rozliczona' : 'Nierozliczona'}
                   tone={work.settled ? undefined : 'amber'}/>
              {work.managerName && <Row label="Kierownik prac" value={work.managerName}/>}
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="card-head">
              <div className="card-title">Lokalizacja</div>
              {work.inheritedLocation && (
                <span className="ml-auto text-[11px] text-ink-400" title="Praca nie ma własnych współrzędnych">
                  punkt klienta
                </span>
              )}
            </div>
            <div className="card-body !p-0">
              {markers.length === 0 ? (
                <div className="p-4 text-[12px] text-ink-400">
                  Brak współrzędnych — ani praca, ani klient nie mają ustawionej pinezki.
                </div>
              ) : (
                <OverviewMap markers={markers} height="240px"/>
              )}
            </div>
          </div>

          {/* Powiązania pokazujemy ZAWSZE, także puste — „na jakiej podstawie tam pojechaliśmy"
              to pytanie, które trzeba móc zadać i dostać uczciwe „nie wiadomo", a nie brak sekcji.
              Linki bramkujemy uprawnieniem: geolog nie otworzy /umowa/:id ani /oferty/:id. */}
          <div className="card">
            <div className="card-head"><div className="card-title">Powiązania</div></div>
            <div className="card-body text-[13px] grid gap-2">
              <LinkRow label="Zlecenie" visible={canEditOrders}
                       to={work.orderId ? `/zlecenia/${work.orderId}` : null}
                       value={work.orderNumber}/>
              <LinkRow label="Oferta" visible={canSeeOffers}
                       to={work.offerId ? `/oferty/${work.offerId}` : null}
                       value={work.offerNumber}/>
              <LinkRow label="Umowa" visible={canSeeContracts}
                       to={work.contractId ? `/umowa/${work.contractId}` : null}
                       value={work.contractNumber}/>
              <LinkRow label="Opracowanie" visible
                       to={work.studyId ? `/studies/${work.studyId}` : null}
                       value={work.studyNumber}/>
              <LinkRow label="Ujęcie" visible
                       to={work.intakeId ? `/intakes/${work.intakeId}` : null}
                       value={work.intakeName}/>
              {canEdit && (
                <button onClick={() => setEditOpen(true)}
                        className="text-[12px] text-water-600 hover:underline text-left mt-1">
                  Zmień powiązania →
                </button>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Podpowiedzi faktur — system nie przypina niczego sam. */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setPickerOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-white rounded-lg shadow-xl w-full max-w-[720px] max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100">
              <h2 className="text-[16px] font-semibold text-ink-900">Przypnij fakturę</h2>
              <button onClick={() => setPickerOpen(false)} className="btn btn-ghost btn-icon text-ink-400">
                <Icon name="x"/>
              </button>
            </div>
            <div className="p-5">
              <div className="text-[12px] text-ink-500 mb-3">
                Faktury sprzedażowe wystawione klientowi <strong>{work.clientName}</strong>, jeszcze
                nieprzypięte do tej pracy. Jedna faktura może objąć kilka prac.
              </div>
              {candidates == null && <div className="text-ink-400">Ładowanie…</div>}
              {candidates?.length === 0 && (
                <div className="text-ink-500 text-[13px]">
                  Brak faktur sprzedażowych dla tego klienta. Sprawdź, czy faktura ma przypisanego
                  kontrahenta — faktury z KSeF dopasowują się po NIP-ie.
                </div>
              )}
              {candidates && candidates.length > 0 && (
                <table className="table-hw">
                  <thead><tr><th>Numer</th><th>Wystawiona</th><th>Netto</th><th>Brutto</th><th></th></tr></thead>
                  <tbody>
                    {candidates.map(c => (
                      <tr key={c.invoiceId} className="hover:bg-ink-50">
                        <td className="font-mono text-[12px]">{c.invoiceNumber ?? c.ksefNumber ?? `#${c.invoiceId}`}</td>
                        <td className="font-mono text-[12px] text-ink-600">{c.issueDate ?? '—'}</td>
                        <td className="font-mono text-[12px]">{formatCurrency(c.totalNet)}</td>
                        <td className="font-mono text-[12px]">{formatCurrency(c.totalGross)}</td>
                        <td className="text-right">
                          <button onClick={() => attach(c.invoiceId)} className="btn btn-primary btn-sm">Przypnij</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <PracaFormModal open={editOpen} initial={work} clients={clients} meta={meta}
                      onClose={() => setEditOpen(false)}
                      onSaved={() => { setEditOpen(false); reload(); }}/>
    </div>
  );
}

/** Wiersz powiązania: link gdy jest, uczciwe „—" gdy brak, nic gdy brak uprawnienia. */
function LinkRow({ label, to, value, visible }: {
  label: string; to: string | null; value: string | null; visible: boolean;
}) {
  if (!visible) return null;
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-500">{label}</span>
      {to && value
        ? <NavLink to={to} className="text-water-600 hover:underline font-mono text-right">{value}</NavLink>
        : <span className="text-ink-300">—</span>}
    </div>
  );
}

function Row({ label, value, hint, tone }: {
  label: string; value: string; hint?: string; tone?: 'red' | 'amber';
}) {
  const color = tone === 'red' ? 'text-red-600 font-semibold'
    : tone === 'amber' ? 'text-amber-700' : 'text-ink-800';
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-500">{label}</span>
      <span className={'text-right ' + color}>
        {value}
        {hint && <div className="text-[11px] text-ink-400 font-normal">{hint}</div>}
      </span>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
